import './styles.css';
import { TooltipIcon } from '@ipfabric/ui-components/Tooltip/TooltipIcon';
import { useClickAway } from '@ipfabric/ui-components/hooks/useClickAway';
import { Button } from 'components/Button/Button';
import { DropdownMenu } from 'components/DropdownMenu/DropdownMenu';
import { FormikSelectField } from 'components/FormikSelectField/FormikSelectField';
import { FormikSwitchField } from 'components/FormikSwitchField/FormikSwitchField';
import { FormikTypeaheadInput } from 'components/FormikTypeaheadInput/FormikTypeaheadInput';
import { useGraphContextSelectors } from 'components/Graph/GraphContext';
import type { UnicastFormValues } from 'components/Graph/GraphSidePanel/GraphPathlookup/UnicastFormValues';
import { usePathLookupIpAutocompleteUnicast } from 'components/Graph/GraphSidePanel/GraphPathlookup/usePathLookupIpAutocompleteUnicast';
import { useLocale } from 'containers/App/LocaleContext';
import type { FormikProps } from 'formik';
import { type FC, useEffect, useRef, useState } from 'react';
import type { SyntheticEvent } from 'react';
import { getBooleanOptionValue } from 'utils/form/getBooleanOptionValue';
import { scrollErrorIntoView } from 'utils/formik/scrollErrorIntoView';
import { ExchangeIcon } from '../icons/ExchangeIcon';
import { InfoIcon } from '../icons/InfoIcon';
import { GraphFirstHopEntryPoints } from './GraphFirstHopEntryPoints';
import { IpModeSwitch } from './IpModeSwitch';
import { PathLookupAdvancedOptions } from './PathLookupAdvancedOptions';
import { PathLookupDropdownMenu } from './PathLookupDropdownMenu';
import type { Vrf } from './fetchVrf';
import { getVrfOption } from './helpers/getVrfOption';
import { isMulticastAddress } from './helpers/isMulticastAddress';

interface Props {
  resetAdvancedOptionsToSelectedPreset: () => void;
  formik: FormikProps<UnicastFormValues>;
  onProtocolChange: (value: string | null) => void;
  onSwitchValues: (switchPorts: boolean) => void;
  vrf: Vrf[];
}

export const GraphUnicastForm: FC<Props> = (props) => {
  const msg = useLocale();
  const formRef = useRef<HTMLFormElement>(null);
  const [swapMenuOpen, setSwapMenuOpen] = useState<boolean>(false);

  const ctxProps = useGraphContextSelectors({
    graphLoading: (s) => s.loading === 'LOADING',
  });

  const startingPointAutocomplete = usePathLookupIpAutocompleteUnicast({
    excludeIps: [props.formik.values.destinationPoint],
  });

  const destinationPointAutocomplete = usePathLookupIpAutocompleteUnicast({
    excludeIps: [props.formik.values.startingPoint],
  });

  const vrfOptions = props.vrf.map((item) => getVrfOption(item, msg));

  const handleStartButtonClick = (e: SyntheticEvent) => {
    e.preventDefault();

    props.formik.submitForm();
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: run on submit
  useEffect(() => {
    // delay to next tic so advanced options can be uncollapsed if needed
    setTimeout(() => {
      if (!props.formik.isValid) {
        scrollErrorIntoView(formRef);
      }
    }, 0);
  }, [props.formik.submitCount]);

  const swapMenuRef = useClickAway<HTMLDivElement>(() => {
    setSwapMenuOpen(false);
  });

  return (
    <form className='py-4' ref={formRef}>
      <div className='px-5'>
        <FormikSwitchField
          name='firstHopAlgorithm'
          label={
            msg.canvasGraph.graphSidePanel.pathlookup.unicast.firstHopAlgorithm
          }
          formik={props.formik}
          options={[
            {
              label:
                msg.canvasGraph.graphSidePanel.pathlookup.unicast.automatic,
              value: 'automatic',
            },
            {
              label:
                msg.canvasGraph.graphSidePanel.pathlookup.unicast.userDefined,
              value: 'userDefined',
            },
          ]}
          size='small'
        />
        {props.formik.values.firstHopAlgorithm === 'automatic' && (
          <FormikSelectField
            label={msg.canvasGraph.graphSidePanel.pathlookup.unicast.vrf}
            formik={props.formik}
            name='vrf'
            options={vrfOptions}
            nonClearable
          />
        )}
        {props.formik.values.firstHopAlgorithm === 'userDefined' && (
          <GraphFirstHopEntryPoints formik={props.formik} />
        )}
        <div className='relative flex items-end'>
          <div className='flex-1'>
            <FormikTypeaheadInput
              label={
                msg.canvasGraph.graphSidePanel.pathlookup.unicast.startingPoint
              }
              autoTrim='all'
              formik={props.formik}
              inputClassName='ipf-input'
              loading={startingPointAutocomplete.loading}
              menuContent={
                <PathLookupDropdownMenu
                  data={startingPointAutocomplete.content}
                  name='startingPoint'
                  formik={props.formik}
                />
              }
              name='startingPoint'
              onFocus={startingPointAutocomplete.search}
              onSearch={startingPointAutocomplete.search}
              placeholder={
                msg.canvasGraph.graphSidePanel.pathlookup.unicast
                  .startingPointPlaceholder
              }
            />
          </div>
          <div className='ml-2.5 pb-6' ref={swapMenuRef}>
            <button
              className='btn btn-default size-10'
              type='button'
              onClick={() => {
                setSwapMenuOpen(!swapMenuOpen);
              }}
            >
              <ExchangeIcon
                title='Swap source and target'
                width='10'
                height='22'
              />
            </button>
            {swapMenuOpen && (
              <DropdownMenu
                className='absolute right-0 top-1'
                items={[
                  {
                    label:
                      msg.canvasGraph.graphSidePanel.pathlookup.unicast
                        .swapOnlyIp,
                    onClick: () => {
                      props.onSwitchValues(false);
                    },
                  },
                  {
                    label:
                      msg.canvasGraph.graphSidePanel.pathlookup.unicast
                        .swapIpWithPorts,
                    onClick: () => {
                      props.onSwitchValues(true);
                    },
                  },
                ]}
                onClose={() => {
                  setSwapMenuOpen(false);
                }}
              />
            )}
          </div>
        </div>
        <div className='relative'>
          <FormikTypeaheadInput
            label={
              msg.canvasGraph.graphSidePanel.pathlookup.unicast.destinationPoint
            }
            autoTrim='all'
            formik={props.formik}
            inputClassName='ipf-input'
            loading={destinationPointAutocomplete.loading}
            menuContent={
              <PathLookupDropdownMenu
                data={destinationPointAutocomplete.content}
                name='destinationPoint'
                formik={props.formik}
              />
            }
            name='destinationPoint'
            onFocus={destinationPointAutocomplete.search}
            onSearch={destinationPointAutocomplete.search}
            placeholder={
              msg.canvasGraph.graphSidePanel.pathlookup.unicast
                .destinationPointPlaceholder
            }
            wrapperClassName='flex-1'
          />
        </div>
        {isMulticastAddress(props.formik.values.destinationPoint) && (
          <div className='mt-2.5 flex items-center'>
            <div className='mr-2.5'>
              <InfoIcon title={null} width='16' height='16' />
            </div>
            <div>
              {
                msg.canvasGraph.graphSidePanel.pathlookup.unicast
                  .multicastDestinationWarning
              }
            </div>
          </div>
        )}
      </div>
      <PathLookupAdvancedOptions
        resetAdvancedOptionsToSelectedPreset={
          props.resetAdvancedOptionsToSelectedPreset
        }
        formik={props.formik}
        onProtocolChange={props.onProtocolChange}
      />
      <div className='px-5 pt-4'>
        <IpModeSwitch formik={props.formik} />
        <FormikSwitchField
          name='securedPath'
          label={
            <>
              {msg.canvasGraph.graphSidePanel.pathlookup.unicast.securedPath}
              <TooltipIcon
                content={
                  msg.canvasGraph.graphSidePanel.pathlookup.unicast
                    .securedPathTooltip
                }
                iconProps={{
                  inline: true,
                  iconStyle: 'solid',
                }}
                size='sm'
                className='ml-2'
              />
            </>
          }
          formik={props.formik}
          options={[
            {
              label: msg.canvasGraph.graphSidePanel.pathlookup.unicast.drop,
              value: true,
            },
            {
              label: msg.canvasGraph.graphSidePanel.pathlookup.unicast.continue,
              value: false,
            },
          ]}
          getOptionValue={getBooleanOptionValue}
          size='small'
        />
      </div>
      <div className='mt-3 flex justify-center'>
        <Button
          variant='primary'
          onClick={handleStartButtonClick}
          size='sm'
          loading={ctxProps.graphLoading}
          type='submit'
        >
          {msg.canvasGraph.graphSidePanel.pathlookup.unicast.btnSubmit}
        </Button>
      </div>
    </form>
  );
};
